import { FRAME_MS, MOTION_FRAMES, NegationCause, resolveColour, type ColourRoleName } from "odyssey-shared";

/**
 * A damage number's motion and look (P2.1-T4; the motion table's `float` and `float-crit`; the design's floating
 * combat text): it rises in whole logical pixels a 40 ms frame at a time over its life and then ends, with no fade
 * and no second flash (ADR-061's motion rules); it says the amount, a critical one with an exclamation mark. Whose
 * it is sets its colour and sign (ADR-079's amendment of 2026-10-03 for P1.9-T66, the author's rulings): damage the
 * player's own character took is `danger` red with a minus sign, a heal over anyone is `heal` green with a plus
 * sign, and a number over another is its damage type's colour, a critical one the critical colour, with no sign. A
 * negated hit says its cause's word instead, in the design's avoid style, the muted text colour at the plain
 * number's size with no sign (ADR-079 decision 6).
 */

/**
 * What a number is drawn from: a hit as the hit feed carries it, less where it landed.
 */
export interface NumberHit {
  readonly amount: number;
  readonly critical: boolean;
  readonly cause: number;
  readonly damageType: number;
  readonly taken: boolean;
  readonly heal: boolean;
}

/**
 * The word a negated hit shows in place of its number, by its cause: drafts for the author. Parried and Deflected wait
 * for a parry mechanic, which the Bible does not have yet.
 */
export const NEGATION_WORDS: Readonly<Record<number, string>> = Object.freeze({
  [NegationCause.Blocked]: "Blocked",
  [NegationCause.Immune]: "Immune",
  [NegationCause.Dodge]: "Dodge",
});

/**
 * How far a number rises over its life, in logical pixels.
 */
export const NUMBER_RISE = 20;

/**
 * How far apart numbers landing together on one character stand across, in logical pixels.
 */
export const NUMBER_SPREAD = 8;

const TINTS: readonly number[] = Object.freeze((["dmg-slashing", "dmg-thermal", "dmg-kinetic", "dmg-poison"] as const satisfies readonly ColourRoleName[]).map(tintOf));
const CRIT_TINT = tintOf("crit");
const WORD_TINT = tintOf("text-muted");
const TAKEN_TINT = tintOf("danger");
const HEAL_TINT = tintOf("heal");

/**
 * A colour role as a tint.
 *
 * @param role The role.
 * @returns The colour as a 24-bit number.
 */
function tintOf(role: ColourRoleName): number {
  return Number.parseInt(resolveColour(role).slice(1), 16);
}

/**
 * How far a number has risen.
 *
 * @param elapsedMs Scene time since it appeared.
 * @param critical Whether it is a critical hit's, which lives `float-crit` rather than `float`.
 * @returns The rise in whole logical pixels, or null once its life has ended.
 */
export function numberRise(elapsedMs: number, critical: boolean): number | null {
  const frames = MOTION_FRAMES[critical ? "float-crit" : "float"];
  const frame = Math.floor(Math.max(0, elapsedMs) / FRAME_MS);
  if (frame >= frames) {
    return null;
  }
  return Math.floor((frame * NUMBER_RISE) / frames);
}

/**
 * The word a hit shows in place of its number.
 *
 * @param cause The hit's negation cause (`NegationCause`).
 * @returns The word, or null for a hit that dealt damage or a cause this page has no word for, which shows its number.
 */
export function negationWord(cause: number): string | null {
  return NEGATION_WORDS[cause] ?? null;
}

/**
 * What a number says: its cause's word for a negated hit, else the amount, a critical one with an exclamation mark,
 * signed "+" for a heal and "-" for damage the player's own character took. A number of 0 carries no sign.
 *
 * @param hit The hit.
 * @returns The text.
 */
export function numberText(hit: NumberHit): string {
  const word = negationWord(hit.cause);
  if (word !== null) {
    return word;
  }
  const sign = hit.amount === 0 ? "" : hit.heal ? "+" : hit.taken ? "-" : "";
  return `${sign}${hit.amount}${hit.critical ? "!" : ""}`;
}

/**
 * A number's tint: the muted text colour for a negated hit's word; the heal colour for a heal over anyone; `danger`
 * for damage the player's own character took, a critical one included; else, over another, the critical colour for
 * a critical hit or its damage type's colour (physical as slashing, a draft until weapons carry their own types;
 * poison `dmg-poison`, venom purple, a stand-in until the designer picks one, ADR-079's amendment for P1.9-T66).
 *
 * @param hit The hit; its damage type is 0 physical, 1 thermal, 2 kinetic or 3 poison, and another reads as physical.
 * @returns The tint.
 */
export function numberTint(hit: NumberHit): number {
  if (negationWord(hit.cause) !== null) {
    return WORD_TINT;
  }
  if (hit.heal) {
    return HEAL_TINT;
  }
  if (hit.taken) {
    return TAKEN_TINT;
  }
  return hit.critical ? CRIT_TINT : (TINTS[hit.damageType] ?? (TINTS[0] as number));
}

/**
 * Where across a number stands among others landing on the same character, by the order they landed.
 *
 * @param order The count of numbers that landed on the character before it and still show.
 * @returns The offset from the character's centre in logical pixels.
 */
export function spreadOf(order: number): number {
  const column = order % 3;
  return column === 0 ? 0 : column === 1 ? NUMBER_SPREAD : -NUMBER_SPREAD;
}
